import axios from "axios";

/**
 * ------------------------------------------------------------------
 * CẤU HÌNH AXIOS CHUẨN ENTERPRISE
 * ------------------------------------------------------------------
 * Bao gồm các tính năng:
 * - Tách biệt 2 instance: 
 *      1. merchantApiClient (Các API merchant gửi filter qua JSON body)
 *      2. globalApiClient (Không đính kèm merchant_id)
 * - Xử lý Anti-pattern: Không gọi Hooks (vd: useAuth) trong file thuần.
 * - Global Response Interceptor (401, 403, 500, Timeout).
 */

const TIMEOUT_MS = 15000; // Timeout 15 giây tránh kẹt UI

// 1. Khởi tạo Base Config chung
const baseConfig = {
  baseURL: process.env.REACT_APP_API_DOMAIN || "",
  timeout: TIMEOUT_MS,
  headers: {
    "Content-Type": "application/json",
  },
};

// 2. Tạo 2 instances riêng biệt
const merchantApiClient = axios.create(baseConfig);
const globalApiClient = axios.create(baseConfig);

// ---------------------------------------------------
// [1] REQUEST INTERCEPTORS
// ---------------------------------------------------

/**
 * Helper: Đính kèm Token vào Header
 */
const attachTokenInterceptor = (config) => {
  // Lấy token trực tiếp từ localStorage (Thay thế cho useAuth hook)
  const token =
    localStorage.getItem("access_token") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("token");
  if (token) {
    config.headers = config.headers || {};
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
};

// Áp dụng Interceptor token cho cả 2 instance
merchantApiClient.interceptors.request.use(attachTokenInterceptor, Promise.reject);
globalApiClient.interceptors.request.use(attachTokenInterceptor, Promise.reject);

//
// ---------------------------------------------------
// [2] RESPONSE INTERCEPTORS (BẮT LỖI GLOBAL)
// ---------------------------------------------------

const onResponseSuccess = (response) => {
  return response;
};

const onResponseError = (error) => {
  // 1. Lỗi Time-out
  if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
    console.warn("[Axios Error] Timeout:", error.message);
    return Promise.reject(
      new Error("Hệ thống phản hồi quá lâu. Vui lòng kiểm tra lại mạng hoặc thử lại sau!")
    );
  }

  // 2. Lỗi mạng chung (Server sập, cúp mạng...)
  if (!error.response) {
    console.error("[Axios Error] Network Error:", error);
    return Promise.reject(new Error("Không thể kết nối đến máy chủ. Vui lòng thử lại!"));
  }

  const { status, data } = error.response;

  // 3. Xử lý các HTTP Status Codes phổ biến
  switch (status) {
    case 401:
      // 401 Unauthorized: Hết hạn / Mất token
      console.warn("[Axios Error] 401 Unauthorized: Bắt buộc đăng nhập lại.");
      
      // Xoá mọi phiên xác thực
      localStorage.removeItem("accessToken");
      localStorage.removeItem("token");
      localStorage.removeItem("access_token");
      
      // Điều hướng về Login an toàn trong Native JS (không dùng react-router hook)
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      break;

    case 403:
      // 403 Forbidden: Chặn truy cập
      console.warn("[Axios Error] 403 Forbidden.");
      return Promise.reject(
        new Error(data?.message || "Bạn không đủ quyền để truy cập tính năng này!")
      );

    case 500:
    case 502:
    case 503:
    case 504:
      // 5xx Server Errors
      console.error(`[Axios Error] Server Error (${status}):`, data);
      return Promise.reject(
        new Error(data?.message || "Máy chủ đang bảo trì hoặc gặp sự cố. Vui lòng thử lại!")
      );

    default:
      // Các lỗi khác như 400 Bad Request, 404 Not Found, 422 Unprocessable...
      // Trả lại exception thô để Component/View trực tiếp xử lý
      break;
  }

  return Promise.reject(error);
};

// Cài đặt Response Interceptor cho cả 2 instance
merchantApiClient.interceptors.response.use(onResponseSuccess, onResponseError);
globalApiClient.interceptors.response.use(onResponseSuccess, onResponseError);

// ---------------------------------------------------
// [3] EXPORTS
// ---------------------------------------------------

/**
 * globalApi: Object helper dành riêng cho các API Route hệ thống (Không gắn merchant).
 * (Thêm wrapper này giữ tính tương thích ngược với source code cũ)
 */
export const globalApi = {
  get: (url, config = {}) => globalApiClient.get(url, config),
  post: (url, data, config = {}) => globalApiClient.post(url, data, config),
  put: (url, data, config = {}) => globalApiClient.put(url, data, config),
  delete: (url, config = {}) => globalApiClient.delete(url, config),
};

/**
 * Xuất cả instance global thuần nếu cần thiết config tuỳ biến riêng.
 */
export { globalApiClient };

/**
 * Mặc định Export merchantApiClient
 * Tương thích ngược: Khi các service cũ "import instance from './axios'" 
 * nó sẽ mapping tới bản có gắn sẵn ID.
 */
export default merchantApiClient;
